import React, { useEffect, useMemo, useRef, useState } from "react";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import {
  collection,
  doc,
  getDoc,
  runTransaction,
  serverTimestamp,
  onSnapshot,
  query,
  where,
} from "firebase/firestore";
import {
  deleteObject,
  getDownloadURL,
  getStorage,
  ref as storageRef,
  uploadBytes,
} from "firebase/storage";
import { db } from "../../firebase";
import { readVehicleDirectory, runVehicleDirectoryTransaction } from "./vehicleDirectory";

const CREATE_MEMBER_ENDPOINT =
  process.env.REACT_APP_CREATE_COMPANY_ENDPOINT ||
  "https://asia-east1-chat3test-59045.cloudfunctions.net/createCompanyAccount";

const MEMBER_ROLES = ["owner", "deputy", "driver"];

export default function DriverManagerDrivers({
  user: propUser,
  account: propAccount,
}) {
  const auth = getAuth();

  const [user, setUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [account, setAccount] = useState(propAccount || null);
  const [accountChecked, setAccountChecked] = useState(!!propAccount);
  const [organization, setOrganization] = useState(null);
  const [activeContract, setActiveContract] = useState(null);
  const [quotaLoading, setQuotaLoading] = useState(true);
  const [quotaMembers, setQuotaMembers] = useState([]);
  const [quotaMembersLoading, setQuotaMembersLoading] = useState(true);
  const [quotaMembersError, setQuotaMembersError] = useState(false);

  const [members, setMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(true);

  const [searchText, setSearchText] = useState("");
  const [selectedDriverId, setSelectedDriverId] = useState("");

  const [createOpen, setCreateOpen] = useState(false);
  const [createRole, setCreateRole] = useState("driver");
  const [creating, setCreating] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editPhotoFile, setEditPhotoFile] = useState(null);
  const [editPhotoPreview, setEditPhotoPreview] = useState("");
  const [editPhotoRemoved, setEditPhotoRemoved] = useState(false);
  const [notice, setNotice] = useState("");

  useEffect(() => {
    if (!notice) return undefined;
    const timer = window.setTimeout(() => setNotice(""), 5000);
    return () => window.clearTimeout(timer);
  }, [notice]);

  const [createForm, setCreateForm] = useState({
    displayName: "",
    phone: "",
    email: "",
    idNumber: "",
    password: "",
    password2: "",
  });

  const [editForm, setEditForm] = useState({
    displayName: "",
    phone: "",
    email: "",
    idNumber: "",
  });

  const [showPw, setShowPw] = useState(false);
  const [showPw2, setShowPw2] = useState(false);

  const userEditedPasswordRef = useRef(false);
  const userEditedPassword2Ref = useRef(false);
  const editPhotoInputRef = useRef(null);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (firebaseUser) => {
      const nextUser = propUser || firebaseUser || null;
      setUser(nextUser);
      setAuthChecked(true);

      if (!nextUser?.uid) {
        setAccount(null);
        setAccountChecked(true);
        return;
      }

      if (propAccount) {
        setAccount(propAccount);
        setAccountChecked(true);
        return;
      }

      setAccountChecked(false);
      try {
        const accountSnapshot = await getDoc(doc(db, "drivers", nextUser.uid));
        setAccount(
          accountSnapshot.exists()
            ? { uid: nextUser.uid, ...accountSnapshot.data() }
            : null,
        );
      } catch (error) {
        console.error("[DriverManagerDrivers] account load error:", error);
        setAccount(null);
      } finally {
        setAccountChecked(true);
      }
    });
    return () => unsub();
  }, [auth, propAccount, propUser]);

  useEffect(() => {
    if (propAccount) {
      setAccount(propAccount);
      setAccountChecked(true);
    }
  }, [propAccount]);

  const signedInUser = propUser || user;
  const orgId = normalizeText(propAccount?.orgId || account?.orgId);
  const quotas = useMemo(
    () => buildMemberQuotas(organization, activeContract, quotaMembers),
    [activeContract, organization, quotaMembers],
  );
  const quotasLoading = quotaLoading || quotaMembersLoading;

  useEffect(() => {
    if (!authChecked || !accountChecked) return;

    if (!orgId) {
      setMembers([]);
      setLoadingMembers(false);
      return;
    }

    const ref = query(collection(db, "drivers"), where("orgId", "==", orgId));
    const unsub = onSnapshot(
      ref,
      (snap) => {
        const list = snap.docs.map((d, idx) => {
          const raw = d.data() || {};
          return normalizeMember({
            id: d.id,
            ...raw,
            __index: idx,
          });
        });

        const sorted = [...list].sort((a, b) =>
          a.displayName.localeCompare(b.displayName, "zh-Hant"),
        );

        setMembers(sorted);
        setSelectedDriverId((currentId) => {
          if (currentId && sorted.some((item) => item.id === currentId)) {
            return currentId;
          }
          const ownMember = sorted.find(
            (item) =>
              item.id === signedInUser?.uid || item.uid === signedInUser?.uid,
          );
          return ownMember?.id || sorted[0]?.id || "";
        });

        setLoadingMembers(false);
      },
      (err) => {
        console.log("[DriverManagerDrivers] members snapshot error:", err);
        setMembers([]);
        setLoadingMembers(false);
      },
    );

    return () => unsub();
  }, [accountChecked, authChecked, orgId, signedInUser?.uid]);

  useEffect(() => {
    if (!orgId) {
      setOrganization(null);
      setActiveContract(null);
      setQuotaLoading(false);
      return undefined;
    }

    setQuotaLoading(true);
    let contractUnsubscribe = null;
    const organizationUnsubscribe = onSnapshot(
      doc(db, "orgs", orgId),
      (snapshot) => {
        const nextOrganization = snapshot.exists()
          ? { id: snapshot.id, ...snapshot.data() }
          : null;
        setOrganization(nextOrganization);

        if (contractUnsubscribe) {
          contractUnsubscribe();
          contractUnsubscribe = null;
        }

        const activeContractId = normalizeText(
          nextOrganization?.activeContractId,
        );
        if (!activeContractId) {
          setActiveContract(null);
          setQuotaLoading(false);
          return;
        }

        contractUnsubscribe = onSnapshot(
          doc(db, "orgs", orgId, "contracts", activeContractId),
          (contractSnapshot) => {
            setActiveContract(
              contractSnapshot.exists() ? contractSnapshot.data() : null,
            );
            setQuotaLoading(false);
          },
          (error) => {
            console.error("[DriverManagerDrivers] contract load error:", error);
            setActiveContract(null);
            setQuotaLoading(false);
          },
        );
      },
      (error) => {
        console.error("[DriverManagerDrivers] organization load error:", error);
        setOrganization(null);
        setActiveContract(null);
        setQuotaLoading(false);
      },
    );

    return () => {
      organizationUnsubscribe();
      if (contractUnsubscribe) contractUnsubscribe();
    };
  }, [orgId]);

  useEffect(() => {
    if (!orgId) {
      setQuotaMembers([]);
      setQuotaMembersLoading(false);
      setQuotaMembersError(false);
      return undefined;
    }

    setQuotaMembersLoading(true);
    setQuotaMembersError(false);
    const unsubscribe = onSnapshot(
      collection(db, "orgs", orgId, "members"),
      (snapshot) => {
        setQuotaMembers(snapshot.docs.map((item) => item.data() || {}));
        setQuotaMembersLoading(false);
        setQuotaMembersError(false);
      },
      (error) => {
        console.error("[DriverManagerDrivers] quota members read error:", error);
        setQuotaMembers([]);
        setQuotaMembersLoading(false);
        setQuotaMembersError(true);
      },
    );

    return () => unsubscribe();
  }, [orgId]);

  useEffect(() => {
    const id = normalizeText(createForm.idNumber).toUpperCase();
    if (!id) return;

    setCreateForm((prev) => ({
      ...prev,
      idNumber: id,
      password: userEditedPasswordRef.current ? prev.password : id,
      password2: userEditedPassword2Ref.current ? prev.password2 : id,
    }));
  }, [createForm.idNumber]);

  const filteredMembers = useMemo(() => {
    const q = searchText.trim().toLowerCase();

    return members.filter((m) => {
      return (
        !q ||
        m.displayName.toLowerCase().includes(q) ||
        m.phone.toLowerCase().includes(q) ||
        m.email.toLowerCase().includes(q) ||
        m.roleText.toLowerCase().includes(q)
      );
    });
  }, [members, searchText]);

  const selectedDriver = useMemo(() => {
    return (
      filteredMembers.find((m) => m.id === selectedDriverId) ||
      members.find((m) => m.id === selectedDriverId) ||
      filteredMembers[0] ||
      members[0] ||
      null
    );
  }, [filteredMembers, members, selectedDriverId]);

  useEffect(() => {
    if (!selectedDriver && filteredMembers.length) {
      setSelectedDriverId(filteredMembers[0].id);
    }
  }, [filteredMembers, selectedDriver]);

  const resetCreateForm = () => {
    setCreateForm({
      displayName: "",
      phone: "",
      email: "",
      idNumber: "",
      password: "",
      password2: "",
    });
    setShowPw(false);
    setShowPw2(false);
    userEditedPasswordRef.current = false;
    userEditedPassword2Ref.current = false;
    setCreateRole("driver");
  };

  const openCreateModal = () => {
    setNotice("");
    resetCreateForm();
    setCreateOpen(true);
  };

  const closeCreateModal = () => {
    if (creating) return;
    setCreateOpen(false);
    resetCreateForm();
  };

  const openEditModal = () => {
    if (!selectedDriver) return;
    const raw = selectedDriver.raw || {};
    setNotice("");
    setEditForm({
      displayName: selectedDriver.displayName,
      phone: selectedDriver.phone,
      email: selectedDriver.email,
      idNumber: str(raw.idNumber),
    });
    setEditPhotoFile(null);
    setEditPhotoRemoved(false);
    setEditPhotoPreview(selectedDriver.photo?.previewURL || "");
    setEditOpen(true);
  };

  const closeEditModal = (force = false) => {
    if (editing && !force) return;
    if (editPhotoPreview.startsWith("blob:")) {
      URL.revokeObjectURL(editPhotoPreview);
    }
    setEditOpen(false);
    setEditPhotoFile(null);
    setEditPhotoRemoved(false);
    setEditPhotoPreview("");
  };

  const handleEditPhotoChange = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      window.alert("請選擇圖片檔案。");
      return;
    }
    if (editPhotoPreview.startsWith("blob:")) {
      URL.revokeObjectURL(editPhotoPreview);
    }
    setEditPhotoFile(file);
    setEditPhotoRemoved(false);
    setEditPhotoPreview(URL.createObjectURL(file));
  };

  const handleRemoveEditPhoto = () => {
    if (editPhotoPreview.startsWith("blob:")) URL.revokeObjectURL(editPhotoPreview);
    setEditPhotoFile(null);
    setEditPhotoPreview("");
    setEditPhotoRemoved(true);
  };

  const handleEditMember = async () => {
    if (!selectedDriver || editing) return;
    const currentOrgId = normalizeText(propAccount?.orgId || account?.orgId);
    const displayName = normalizeText(editForm.displayName);
    const email = normalizeText(editForm.email).toLowerCase();
    if (!currentOrgId || !selectedDriver.id)
      return window.alert("缺少成員資料。");
    if (!displayName) return window.alert("請輸入成員姓名。");
    if (!looksLikeEmail(email)) return window.alert("Email 格式不正確。");

    try {
      setEditing(true);
      const currentUser = auth.currentUser;
      if (!currentUser)
        throw new Error("目前沒有取得登入狀態，請重新登入後再試。");

      let photo = editPhotoRemoved ? null : selectedDriver.photo || null;
      const newlyUploadedPhotoPaths = [];
      if (editPhotoFile) {
        const photoAssetId = makeMemberPhotoUuid();
        const originalBlob = await compressMemberPhoto(editPhotoFile, 1280, 0.78);
        const previewBlob = await compressMemberPhoto(originalBlob, 1280, 0.72, 0.3);
        const photoRoot = `orgs/${currentOrgId}/drivers/${selectedDriver.id}/photos/${photoAssetId}`;
        const originalPath = `${photoRoot}/original`;
        const previewPath = `${photoRoot}/preview`;
        const storage = getStorage();
        try {
          await uploadBytes(storageRef(storage, originalPath), originalBlob, {
            contentType: "image/jpeg",
            cacheControl: "public,max-age=31536000",
          });
          newlyUploadedPhotoPaths.push(originalPath);
          await uploadBytes(storageRef(storage, previewPath), previewBlob, {
            contentType: "image/jpeg",
            cacheControl: "public,max-age=31536000",
          });
          newlyUploadedPhotoPaths.push(previewPath);
          const previewURL = await getDownloadURL(storageRef(storage, previewPath));
          photo = {
            assetId: photoAssetId,
            originalPath,
            previewPath,
            previewURL,
            contentType: "image/jpeg",
            originalSizeBytes: Number(originalBlob.size) || 0,
            previewSizeBytes: Number(previewBlob.size) || 0,
            previewScale: 0.3,
          };
        } catch (uploadError) {
          await Promise.allSettled(newlyUploadedPhotoPaths.map((path) =>
            deleteObject(storageRef(storage, path)),
          ));
          throw uploadError;
        }
      }

      try {
        const token = await currentUser.getIdToken(true);
        const response = await fetch(CREATE_MEMBER_ENDPOINT, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            action: "updateMember",
            orgId: currentOrgId,
            uid: selectedDriver.id,
            member: {
              displayName,
              phone: normalizeText(editForm.phone),
              email,
              idNumber: normalizeText(editForm.idNumber).toUpperCase(),
              ...((editPhotoFile || editPhotoRemoved) ? {photo} : {}),
            },
          }),
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(payload?.message || "更新成員失敗，請稍後再試。");
        }
      } catch (saveError) {
        await Promise.allSettled(newlyUploadedPhotoPaths.map((path) =>
          deleteObject(storageRef(getStorage(), path)),
        ));
        throw saveError;
      }

      if (editPhotoFile || editPhotoRemoved) {
        const oldPhoto = selectedDriver.photo || {};
        const oldPaths = new Set([
          oldPhoto.originalPath,
          oldPhoto.previewPath,
        ].map((path) => str(path)).filter((path) =>
          path.startsWith(`orgs/${currentOrgId}/drivers/${selectedDriver.id}/`) &&
          !newlyUploadedPhotoPaths.includes(path),
        ));
        await Promise.allSettled([...oldPaths].map((path) =>
          deleteObject(storageRef(getStorage(), path)),
        ));
      }

      closeEditModal(true);
      setNotice(`已更新 ${displayName} 的資料。`);
    } catch (error) {
      console.error("[DriverManagerDrivers] update member error:", error);
      window.alert(error?.message || "更新成員失敗，請稍後再試。");
    } finally {
      setEditing(false);
    }
  };

  const handleCreateMember = async () => {
    const currentOrgId = normalizeText(propAccount?.orgId || account?.orgId);
    const displayName = normalizeText(createForm.displayName);
    const phone = normalizeText(createForm.phone);
    const email = normalizeText(createForm.email).toLowerCase();
    const idNumber = normalizeText(createForm.idNumber).toUpperCase();
    const password = normalizeText(createForm.password);
    const password2 = normalizeText(createForm.password2);
    const selectedQuota = quotas[createRole];

    if (!signedInUser?.uid) return window.alert("請先登入");
    if (!currentOrgId) {
      return window.alert(
        "無法建立成員。請確認組織編號。",
      );
    }
    if (!selectedQuota || selectedQuota.used >= selectedQuota.limit) {
      return window.alert(`${memberRoleLabel(createRole)}名額已滿，無法建立。`);
    }
    if (!displayName) return window.alert("請輸入成員姓名");
    if (!email) return window.alert("請輸入 Email");
    if (!looksLikeEmail(email)) return window.alert("Email 格式不正確");
    if (!idNumber) return window.alert("請輸入身分證字號");
    if (idNumber.length < 6) return window.alert("身分證字號格式似乎不正確");
    if (!password) return window.alert("請輸入密碼");
    if (password.length < 6) return window.alert("密碼至少 6 碼");
    if (!password2) return window.alert("請輸入驗證密碼");
    if (password !== password2) return window.alert("驗證密碼不一致");

    try {
      setCreating(true);
      const currentUser = auth.currentUser;
      if (!currentUser)
        throw new Error("目前沒有取得登入狀態，請重新登入後再試。");
      const token = await currentUser.getIdToken(true);
      const response = await fetch(CREATE_MEMBER_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          action: "createAdmin",
          orgId: currentOrgId,
          admin: {
            role: createRole,
            displayName,
            phone,
            email,
            idNumber,
            password,
          },
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.message || "後端建立成員失敗，請稍後再試。");
      }

      setCreateOpen(false);
      resetCreateForm();
      setNotice(
        `已建立${memberRoleLabel(createRole)}。`,
      );
    } catch (error) {
      console.error("[DriverManagerDrivers] create member error:", error);
      window.alert(error?.message || "建立成員失敗，請稍後再試。");
    } finally {
      setCreating(false);
    }
  };

  if (!authChecked) {
    return (
      <div style={styles.centerWrap}>
        <div style={styles.spinnerLarge} />
        <div style={styles.centerSub}>驗證登入狀態中…</div>
      </div>
    );
  }

  if (!signedInUser?.uid) {
    return (
      <div style={styles.centerWrap}>
        <div style={styles.centerTitle}>成員管理</div>
        <div style={styles.centerSub}>請先登入帳號</div>
      </div>
    );
  }

  if (!accountChecked) {
    return (
      <div style={styles.centerWrap}>
        <div style={styles.spinnerLarge} />
        <div style={styles.centerSub}>讀取公司資料中…</div>
      </div>
    );
  }

  if (!orgId) {
    return (
      <div style={styles.centerWrap}>
        <div style={styles.centerTitle}>成員管理</div>
        <div style={styles.centerSub}>
          目前登入帳號尚未取得 orgId，請確認 drivers/登入 UID 文件。
        </div>
      </div>
    );
  }

  return (
    <>
      <div style={styles.root}>
        {notice ? (
          <div
            role="status"
            aria-live="polite"
            style={{
              position: "fixed",
              zIndex: 12000,
              top: 20,
              right: 20,
              display: "flex",
              alignItems: "center",
              gap: 12,
              maxWidth: "min(420px, calc(100vw - 32px))",
              padding: "13px 14px 13px 16px",
              border: "1px solid #cce7d8",
              borderRadius: 12,
              background: "rgba(248, 255, 251, .97)",
              boxShadow: "0 12px 32px rgba(27, 54, 41, .16)",
              color: "#287a55",
              fontSize: 13,
              lineHeight: 1.45,
              backdropFilter: "blur(10px)",
            }}
          >
            <span
              aria-hidden="true"
              style={{
                display: "grid",
                placeItems: "center",
                flex: "0 0 22px",
                width: 22,
                height: 22,
                borderRadius: "50%",
                background: "#ddf3e6",
                color: "#287a55",
                fontWeight: 800,
              }}
            >
              ✓
            </span>
            <span style={{ flex: 1 }}>{notice}</span>
            <button
              type="button"
              aria-label="關閉通知"
              onClick={() => setNotice("")}
              style={{
                display: "grid",
                placeItems: "center",
                flex: "0 0 25px",
                width: 25,
                height: 25,
                border: 0,
                borderRadius: 7,
                background: "transparent",
                color: "#658271",
                fontSize: 18,
                cursor: "pointer",
              }}
            >
              ×
            </button>
          </div>
        ) : null}

        <div style={styles.mainGrid}>
          <section style={styles.leftPanel}>
            <div style={styles.listTopRow}>
              <div style={styles.listTitleWrap}>
                <div style={styles.listTitleRow}>
                  <div style={styles.panelTitle}>成員名單</div>
                  <div style={styles.inlineCountPill}>
                    共 {members.length} 位成員
                  </div>
                  <div style={styles.headerActions}>
                    <button
                      type="button"
                      style={styles.primaryBtn}
                      onClick={openCreateModal}
                    >
                      ＋ 新增成員
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div style={styles.filterBarSingle}>
              <div style={styles.searchWrap}>
                <SearchIcon />
                <input
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  placeholder="搜尋姓名、電話、Email、角色"
                  style={styles.searchInput}
                />
                {!!searchText && (
                  <button
                    type="button"
                    style={styles.clearSearchBtn}
                    onClick={() => setSearchText("")}
                  >
                    <CloseIcon />
                  </button>
                )}
              </div>
            </div>

            <div style={styles.listScrollArea}>
              {loadingMembers ? (
                <div style={styles.loadingBox}>
                  <div style={styles.spinner} />
                  <div style={styles.loadingText}>載入成員資料中…</div>
                </div>
              ) : !filteredMembers.length ? (
                <div style={styles.emptyBox}>
                  <div style={styles.emptyTitle}>找不到符合條件的成員</div>
                  <div style={styles.emptySub}>請換個關鍵字，或先新增成員</div>
                </div>
              ) : (
                <div style={styles.driverList}>
                  {filteredMembers.map((member) => {
                    const active = selectedDriver?.id === member.id;

                    return (
                      <button
                        key={member.id}
                        type="button"
                        onClick={() => setSelectedDriverId(member.id)}
                        style={{
                          ...styles.driverRow,
                          ...(active ? styles.driverRowActive : {}),
                        }}
                      >
                        <div style={styles.driverRowLeft}>
                          <div
                            style={{
                              ...styles.avatar,
                              background: member.avatarBg,
                            }}
                          >
                            {member.photo?.previewURL ? (
                              <img
                                src={member.photo.previewURL}
                                alt={member.displayName}
                                style={styles.avatarImg}
                              />
                            ) : (
                              <span style={styles.avatarText}>
                                {member.displayName?.[0] || "司"}
                              </span>
                            )}
                          </div>

                          <div
                            style={{
                              flex: 1,
                              minWidth: 0,
                              textAlign: "left",
                            }}
                          >
                            <div style={styles.driverNameRow}>
                              <span style={styles.driverName}>
                                {member.displayName}
                              </span>
                              <RoleBadge role={member.roleKey} />
                            </div>

                            <div style={styles.driverMeta}>
                              {member.roleText}｜{member.phone || "電話未填"}
                            </div>

                            <div style={styles.driverSubLine}>
                              {member.email || "Email 未填"}
                            </div>
                          </div>
                        </div>

                        <ChevronRightIcon />
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </section>

          <section style={styles.rightPanel}>
            {!selectedDriver ? (
              <div style={styles.emptyDetailWrap}>
                <div style={styles.emptyTitle}>尚未選擇成員</div>
                <div style={styles.emptySub}>請先從左側名單選擇一位成員</div>
              </div>
            ) : (
              <>
                <div style={styles.detailHero}>
                  <div style={styles.detailHeroLeft}>
                    <div
                      style={{
                        ...styles.bigAvatar,
                        background: selectedDriver.avatarBg,
                      }}
                    >
                      {selectedDriver.photo?.previewURL ? (
                        <img
                          src={selectedDriver.photo.previewURL}
                          alt={selectedDriver.displayName}
                          style={styles.avatarImg}
                        />
                      ) : (
                        <span style={styles.bigAvatarText}>
                          {selectedDriver.displayName?.[0] || "司"}
                        </span>
                      )}
                    </div>

                    <div style={{ minWidth: 0 }}>
                      <div style={styles.detailNameRow}>
                        <h2 style={styles.detailName}>
                          {selectedDriver.displayName}
                        </h2>
                        <RoleBadge role={selectedDriver.roleKey} />
                      </div>

                      <div style={styles.detailRole}>
                        {selectedDriver.roleText}成員
                      </div>
                      <div style={styles.detailEmailLine}>
                        {selectedDriver.email || "Email 未設定"}
                      </div>
                    </div>
                  </div>

                  <div style={styles.detailHeroActions}>
                    <button
                      type="button"
                      style={styles.softBtn}
                      onClick={openEditModal}
                    >
                      編輯資料
                    </button>
                  </div>
                </div>

                <div style={styles.detailScrollArea}>
                  <div style={styles.detailGrid}>
                    {isBindingDriver(selectedDriver.raw) && orgId ? (
                      <DriverVehicleBinding
                        key={`${orgId}:${selectedDriver.uid}`}
                        orgId={orgId}
                        member={selectedDriver}
                        members={members}
                      />
                    ) : null}
                    <InfoCard
                      title="基本資料"
                      full
                      rows={[
                        { label: "姓名", value: selectedDriver.displayName },
                        {
                          label: "電話",
                          value: selectedDriver.phone || "未設定",
                        },
                        {
                          label: "Email",
                          value: selectedDriver.email || "未設定",
                        },
                        { label: "角色", value: selectedDriver.roleText },
                      ]}
                    />

                  </div>
                </div>
              </>
            )}
          </section>
        </div>
      </div>

      {editOpen ? (
        <div style={styles.modalBackdrop}>
          <div style={styles.modalCard}>
            <div style={styles.modalHeader}>
              <div>
                <div style={styles.modalTitle}>編輯成員資料</div>
                <div style={styles.modalHint}>
                  主管理員可以更新成員的基本資料與照片。
                </div>
              </div>
              <button
                type="button"
                style={styles.modalCloseBtn}
                onClick={closeEditModal}
              >
                <CloseIcon />
              </button>
            </div>

            <div style={styles.modalBody}>
              <div style={styles.photoEditRow}>
                <div style={styles.photoEditPreview}>
                  {editPhotoPreview ? (
                    <img
                      src={editPhotoPreview}
                      alt="成員預覽"
                      style={styles.photoEditImg}
                    />
                  ) : (
                    <span style={styles.bigAvatarText}>
                      {selectedDriver?.displayName?.[0] || "司"}
                    </span>
                  )}
                </div>
                <div style={styles.photoEditCopy}>
                  <strong>成員照片</strong>
                  {editPhotoRemoved ? <small>儲存後移除目前照片</small> : null}
                </div>
                <button
                  type="button"
                  style={styles.softBtn}
                  onClick={() => editPhotoInputRef.current?.click()}
                  disabled={editing}
                >
                  選擇照片
                </button>
                {selectedDriver?.photo?.previewPath && !editPhotoRemoved ? (
                  <button
                    type="button"
                    style={styles.softBtn}
                    onClick={handleRemoveEditPhoto}
                    disabled={editing}
                  >
                    移除照片
                  </button>
                ) : null}
                <input
                  ref={editPhotoInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleEditPhotoChange}
                  style={styles.hiddenFileInput}
                />
              </div>

              <div style={styles.modalGrid}>
                <Field label="姓名（必填）">
                  <input
                    value={editForm.displayName}
                    onChange={(event) =>
                      setEditForm((prev) => ({
                        ...prev,
                        displayName: event.target.value,
                      }))
                    }
                    style={styles.input}
                  />
                </Field>
                <Field label="電話">
                  <input
                    value={editForm.phone}
                    onChange={(event) =>
                      setEditForm((prev) => ({
                        ...prev,
                        phone: event.target.value,
                      }))
                    }
                    style={styles.input}
                  />
                </Field>
              </div>

              <div style={styles.modalGrid}>
                <Field label="Email（必填）">
                  <input
                    type="email"
                    value={editForm.email}
                    disabled
                    readOnly
                    aria-readonly="true"
                    style={{ ...styles.input, ...styles.inputDisabled }}
                  />
                </Field>
                <Field label="身分證字號">
                  <input
                    value={editForm.idNumber}
                    onChange={(event) =>
                      setEditForm((prev) => ({
                        ...prev,
                        idNumber: event.target.value.toUpperCase(),
                      }))
                    }
                    style={styles.input}
                  />
                </Field>
              </div>

              <Field label="角色">
                <div style={styles.readOnlyRole}>
                  <RoleBadge role={selectedDriver?.roleKey} />
                </div>
              </Field>
            </div>

            <div style={styles.modalFooter}>
              <button
                type="button"
                style={styles.cancelBtn}
                onClick={closeEditModal}
                disabled={editing}
              >
                取消
              </button>
              <button
                type="button"
                style={{
                  ...styles.confirmBtn,
                  ...(editing ? styles.confirmBtnDisabled : {}),
                }}
                onClick={handleEditMember}
                disabled={editing}
              >
                {editing ? "儲存中…" : "儲存變更"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {createOpen ? (
        <div style={styles.modalBackdrop}>
          <div style={styles.modalCard}>
            <div style={styles.modalHeader}>
              <div>
                <div style={styles.modalTitle}>新增成員</div>
              </div>

              <button
                type="button"
                style={styles.modalCloseBtn}
                onClick={closeCreateModal}
              >
                <CloseIcon />
              </button>
            </div>

            <div style={styles.modalBody}>
              <div style={styles.roleSelectTitle}>建立哪一種成員？</div>
              <div style={styles.roleChoices}>
                {MEMBER_ROLES.map((role) => {
                  const quota = quotas[role];
                  const quotaFull = quota && quota.used >= quota.limit;
                  const quotaBlocked = quotaFull || quotaMembersError;
                  return (
                    <button
                      key={role}
                      type="button"
                      style={{
                        ...styles.roleChoice,
                        ...(createRole === role ? styles.roleChoiceActive : {}),
                        ...(quotaBlocked ? styles.roleChoiceDisabled : {}),
                      }}
                      onClick={() => {
                        if (!quotasLoading && !quotaBlocked) setCreateRole(role);
                      }}
                      disabled={creating || quotasLoading || quotaBlocked}
                    >
                      <div style={styles.roleChoiceTopline}>
                        <RoleBadge role={role} />
                        <strong>{quotaText(quota, quotasLoading, quotaMembersError)}</strong>
                      </div>
                      <span>{memberRoleLabel(role)}</span>
                    </button>
                  );
                })}
              </div>

              <div style={styles.modalGrid}>
                <Field label={`${memberRoleLabel(createRole)}姓名（必填）`}>
                  <input
                    value={createForm.displayName}
                    onChange={(e) =>
                      setCreateForm((prev) => ({
                        ...prev,
                        displayName: e.target.value,
                      }))
                    }
                    placeholder="請輸入姓名"
                    style={styles.input}
                  />
                </Field>

                <Field label="電話（可選）">
                  <input
                    value={createForm.phone}
                    onChange={(e) =>
                      setCreateForm((prev) => ({
                        ...prev,
                        phone: e.target.value,
                      }))
                    }
                    placeholder="請輸入電話"
                    style={styles.input}
                  />
                </Field>
              </div>

              <Field label="Email（必填）">
                <input
                  value={createForm.email}
                  onChange={(e) =>
                    setCreateForm((prev) => ({
                      ...prev,
                      email: e.target.value,
                    }))
                  }
                  placeholder="請輸入 Email"
                  style={styles.input}
                />
              </Field>

              <Field label="身分證字號（必填）">
                <input
                  value={createForm.idNumber}
                  onChange={(e) =>
                    setCreateForm((prev) => ({
                      ...prev,
                      idNumber: String(e.target.value || "").toUpperCase(),
                    }))
                  }
                  placeholder="例：A123456789"
                  style={styles.input}
                />
              </Field>

              <SecureField
                label="密碼（預設 = 身分證字號）"
                value={createForm.password}
                visible={showPw}
                onToggleVisible={() => setShowPw((v) => !v)}
                onUserEdit={() => {
                  userEditedPasswordRef.current = true;
                }}
                onChange={(value) =>
                  setCreateForm((prev) => ({ ...prev, password: value }))
                }
                placeholder="至少 6 碼"
              />

              <SecureField
                label="驗證密碼"
                value={createForm.password2}
                visible={showPw2}
                onToggleVisible={() => setShowPw2((v) => !v)}
                onUserEdit={() => {
                  userEditedPassword2Ref.current = true;
                }}
                onChange={(value) =>
                  setCreateForm((prev) => ({ ...prev, password2: value }))
                }
                placeholder="請再輸入相同密碼"
              />

            </div>

            <div style={styles.modalFooter}>
              <button
                type="button"
                style={styles.cancelBtn}
                onClick={closeCreateModal}
              >
                取消
              </button>
              <button
                type="button"
                style={{
                  ...styles.confirmBtn,
                  ...(creating ? styles.confirmBtnDisabled : {}),
                }}
                onClick={handleCreateMember}
                disabled={creating}
              >
                {creating ? "建立中…" : "建立並加入"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}

// Binding access deliberately does not use normalizeRole's default-to-driver fallback.
function isBindingDriver(member) {
  return normalizeText(member?.role || member?.memberRole || member?.orgRole).toLowerCase() === "driver";
}

function currentVehicleVersion(data) {
  const at = data?.currentVehicleUpdatedAt;
  return JSON.stringify([data?.currentVehicleId || null,
    at?.seconds ?? null, at?.nanoseconds ?? null]);
}

function bindingVehicleLabel(vehicle) {
  const identity = vehicle.plateNumber || vehicle.displayName || vehicle.assetCode || vehicle.id;
  const model = [vehicle.make, vehicle.model].filter(Boolean).join(" ");
  return [identity, model].filter(Boolean).join(" · ");
}

function DriverVehicleBinding({ orgId, member, members = [] }) {
  const [binding, setBinding] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [currentLabel, setCurrentLabel] = useState("");
  const [currentVehicle, setCurrentVehicle] = useState(null);
  const [currentAttachments, setCurrentAttachments] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [assetIndex, setAssetIndex] = useState({});
  const driversFor = (id) => members.filter(person => normalizeText(person.currentVehicleId) === id)
    .map(person => ({ uid: person.uid || person.id, name: person.displayName || person.name || `成員 ${person.uid || person.id}` }));
  const attachmentLabel = (id) => assetIndex[id] ? bindingVehicleLabel({ ...assetIndex[id], id }) : `${id}（摘要缺漏）`;

  const [editor, setEditor] = useState(null);
  const [vehicleId, setVehicleId] = useState("");
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [optionsLoaded, setOptionsLoaded] = useState(false);
  const [optionsError, setOptionsError] = useState("");
  const [vehicleSearch, setVehicleSearch] = useState("");
  const optionRequest = useRef(0);
  const pickerDialog = useRef(null);
  const pickerTrigger = useRef(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const mounted = useRef(false);
  const busy = useRef(false);
  const activeVehicleId = normalizeText(binding?.currentVehicleId);
  const filteredVehicles = useMemo(() => {
    const keyword = normalizeText(vehicleSearch).toLocaleLowerCase();
    return vehicles.filter((item) => !keyword || [item.plateNumber, item.assetCode, item.displayName, item.make, item.model, ...driversFor(item.id).map(driver => driver.name), ...(item.trailerIds || []).map(attachmentLabel), ...(item.containerIds || []).map(attachmentLabel)]
      .filter(Boolean).join(" ").toLocaleLowerCase().includes(keyword));
  }, [vehicles, vehicleSearch, members, assetIndex]);

  useEffect(() => {
    mounted.current = true;
    const unsubscribe = onSnapshot(
      doc(db, "drivers", member.id || member.uid),
      (snapshot) => {
        setBinding(snapshot.exists() ? snapshot.data() : null);
        setLoaded(snapshot.exists());
      },
      (err) => {
        console.error("[DriverVehicleBinding] driver profile read:", err);
        setLoaded(false);
        setError("無法讀取成員目前車輛，請確認存取權限後重新開啟此成員。");
      },
    );
    return () => { mounted.current = false; optionRequest.current += 1; unsubscribe(); };
  }, [orgId, member.uid]);

  useEffect(() => {
    let cancelled = false;
    setCurrentLabel("");
    setCurrentVehicle(null);
    setCurrentAttachments([]);
    if (activeVehicleId) {
      (async () => {
        try {
          const vehicleSnapshot = await getDoc(doc(db, "orgs", orgId, "vehicles", activeVehicleId));
          if (cancelled) return;
          if (!vehicleSnapshot.exists()) {
            setCurrentLabel(`${activeVehicleId}（資產已不存在，可解除綁定）`);
            return;
          }
          const vehicle = { ...vehicleSnapshot.data(), id: vehicleSnapshot.id };
          setCurrentVehicle(vehicle);
          setCurrentLabel(bindingVehicleLabel(vehicle));
          try {
            const bindingSnapshot = await getDoc(doc(db, "orgs", orgId, "vehicleBindings", activeVehicleId));
            if (cancelled || !bindingSnapshot.exists()) return;
            const bindingData = bindingSnapshot.data() || {};
            const related = [
              ...(Array.isArray(bindingData.trailerIds) ? bindingData.trailerIds : []).map((id) => ({ id, kind: "trailer", label: "拖車" })),
              ...(Array.isArray(bindingData.containerIds) ? bindingData.containerIds : []).map((id) => ({ id, kind: "container", label: "容器" })),
            ];
            const attachments = await Promise.all(related.map(async (item) => {
              try {
                const snapshot = await getDoc(doc(db, "orgs", orgId, "vehicles", item.id));
                return { ...item, ...(snapshot.exists() ? snapshot.data() : {}), exists: snapshot.exists() };
              } catch {
                return { ...item, exists: false };
              }
            }));
            if (!cancelled) setCurrentAttachments(attachments);
          } catch (attachmentError) {
            console.warn("[DriverVehicleBinding] attachment read:", attachmentError);
          }
        } catch {
          if (!cancelled) setCurrentLabel(`${activeVehicleId}（無法讀取車輛資料）`);
        }
      })();
    }
    return () => { cancelled = true; };
  }, [orgId, activeVehicleId]);

  useEffect(() => {
    if (!editor || !pickerDialog.current) return undefined;
    const dialog = pickerDialog.current;
    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    if (!dialog.open) dialog.showModal();
    document.body.style.overflow = "hidden";
    return () => {
      dialog.close();
      document.body.style.overflow = previousOverflow;
      if (previousFocus?.isConnected) previousFocus.focus();
      else pickerTrigger.current?.focus();
    };
  }, [Boolean(editor)]);

  async function loadVehicleOptions() {
    const request = ++optionRequest.current;
    setOptionsLoaded(false);
    setOptionsError("");
    setVehicles([]);
    setLoadingOptions(true);
    try {
      // The shared reader follows summary pages; never query the vehicles collection here.
      const directory = await readVehicleDirectory(db, orgId);
      if (!mounted.current || request !== optionRequest.current) return;
      setAssetIndex(Object.fromEntries(directory.items.map(item => [item.assetId, item])));
      setVehicles(directory.items.filter(item => item.assetKind === "vehicle")
        .map(item => ({ ...item, status: normalizeText(item.status).toUpperCase(), id: item.assetId }))
        .sort((a, b) => bindingVehicleLabel(a).localeCompare(bindingVehicleLabel(b), "zh-Hant")));
      setOptionsLoaded(true);
    } catch (err) {
      if (mounted.current && request === optionRequest.current) {
        setOptionsError(err.message || "無法載入車輛清單，請重試。");
      }
    } finally {
      if (mounted.current && request === optionRequest.current) setLoadingOptions(false);
    }
  }

  function openEditor() {
    if (busy.current || !loaded || !isBindingDriver(member.raw)) return;
    setError("");
    setMessage("");
    setEditor({ version: currentVehicleVersion(binding) });
    setVehicleId(activeVehicleId);
    setVehicleSearch("");
    loadVehicleOptions();
  }

  function closeEditor() {
    optionRequest.current += 1;
    setLoadingOptions(false);
    setEditor(null);
    setOptionsError("");
    setError("");
  }

  async function saveBinding(nextVehicleId, expectedVersion) {
    if (busy.current || !loaded || !isBindingDriver(member.raw)) return;
    if (!nextVehicleId && !window.confirm(`確定解除 ${member.displayName} 目前使用的車輛？`)) return;
    busy.current = true;
    setSaving(true);
    setError("");
    setMessage("");
    try {
      const actorUid = getAuth().currentUser?.uid;
      if (!actorUid) throw new Error("請重新登入後再操作。");
      const memberRef = doc(db, "drivers", member.id || member.uid);
      const eventRef = doc(collection(db, "orgs", orgId, "fleetEvents"));
      await runVehicleDirectoryTransaction(db, orgId, actorUid, async (transaction) => {
        const actorSnapshot = await transaction.get(doc(db, "drivers", actorUid));
        const memberSnapshot = await transaction.get(memberRef);
        const actor = actorSnapshot.data();
        const driver = memberSnapshot.data();
        const actorRole = normalizeText(actor?.role || actor?.memberRole || actor?.orgRole).toLowerCase();
        if (!actorSnapshot.exists() || actor?.orgId !== orgId ||
            !["owner", "primary", "primary_admin", "deputy", "deputy_admin"].includes(actorRole)) {
          throw new Error("只有同公司的管理員可以操作車輛指派。");
        }
        if (!memberSnapshot.exists() || driver?.orgId !== orgId || !isBindingDriver(driver) ||
            normalizeText(driver.uid || memberSnapshot.id) !== member.uid) {
          throw new Error("這位成員目前不是本公司的成員，請重新整理。");
        }
        if (currentVehicleVersion(driver) !== expectedVersion) {
          throw new Error("成員目前車輛已被其他操作更新，請取消後重新選擇。");
        }
        const previousVehicleId = normalizeText(driver.currentVehicleId);
        if (previousVehicleId === nextVehicleId) return;

        const nextVehicleRef = nextVehicleId ? doc(db, "orgs", orgId, "vehicles", nextVehicleId) : null;
        const vehicleSnapshot = nextVehicleRef ? await transaction.get(nextVehicleRef) : null;
        const nextAsset = vehicleSnapshot?.data() || {};
        const nextAssetStatus = normalizeText(nextAsset.status).toUpperCase();
        if (nextVehicleId && (!vehicleSnapshot?.exists() || nextAsset.assetKind !== "vehicle")) {
          throw new Error("所選資產不是有效車輛，請重新選擇。");
        }
        if (nextVehicleId && nextAssetStatus !== "PARKED") {
          throw new Error(nextAssetStatus === "IN_USE" ? "這輛車的狀態是使用中，請先解除原指派。" : "只有狀態為停放中的車輛可以新指派。");
        }

        const previousVehicleRef = previousVehicleId ? doc(db, "orgs", orgId, "vehicles", previousVehicleId) : null;
        const previousVehicleSnapshot = previousVehicleRef ? await transaction.get(previousVehicleRef) : null;
        const staleDriverUid = normalizeText(nextAsset.currentDriverId);
        const staleDriverRef = nextVehicleId && staleDriverUid && staleDriverUid !== member.uid
          ? doc(db, "drivers", staleDriverUid) : null;
        const staleDriverSnapshot = staleDriverRef ? await transaction.get(staleDriverRef) : null;
        const summaryItems = transaction.getSummary();
        if (nextVehicleId && !summaryItems[nextVehicleId]) throw new Error("找不到車輛摘要，請先重新整理資產清單。");

        const actorName = normalizeText(actor?.displayName || actor?.name || actor?.fullName || "");
        const driverName = member.displayName || driver.displayName || driver.name || "";
        if (previousVehicleId && previousVehicleId !== nextVehicleId && previousVehicleSnapshot?.exists()) {
          const oldAsset = previousVehicleSnapshot.data() || {};
          const oldDriverUid = normalizeText(oldAsset.currentDriverId);
          const oldStillAssignedToAnotherDriver = Boolean(oldDriverUid && oldDriverUid !== member.uid);
          const oldStoredStatus = normalizeText(oldAsset.status).toUpperCase();
          const oldStatus = oldStillAssignedToAnotherDriver ? oldStoredStatus
            : ["MAINTENANCE", "RETIRED"].includes(oldStoredStatus) ? oldStoredStatus : "PARKED";
          if (!oldStillAssignedToAnotherDriver) {
            transaction.set(previousVehicleRef, { currentDriverId: null, currentDriverName: "", status: oldStatus,
              updatedAt: serverTimestamp(), updatedByUid: actorUid }, { merge: true });
            if (summaryItems[previousVehicleId]) summaryItems[previousVehicleId] = {
              ...summaryItems[previousVehicleId], currentDriverId: "", currentDriverName: "", status: oldStatus,
            };
          }
        }
        // currentDriverId is a mirrored lookup field only. If a parked vehicle has a stale pointer,
        // clear the matching driver's reverse pointer before writing the new assignment.
        if (staleDriverSnapshot?.exists() && normalizeText(staleDriverSnapshot.data()?.currentVehicleId) === nextVehicleId) {
          transaction.set(staleDriverRef, { currentVehicleId: null, currentVehicleUpdatedAt: serverTimestamp() }, { merge: true });
        }
        if (nextVehicleId) {
          transaction.set(nextVehicleRef, { currentDriverId: member.uid, currentDriverName: driverName, status: "IN_USE",
            updatedAt: serverTimestamp(), updatedByUid: actorUid }, { merge: true });
          summaryItems[nextVehicleId] = { ...summaryItems[nextVehicleId], currentDriverId: member.uid,
            currentDriverName: driverName, status: "IN_USE" };
        }
        transaction.set(memberRef, { currentVehicleId: nextVehicleId || null,
          currentVehicleUpdatedAt: serverTimestamp() }, { merge: true });
        if (previousVehicleId || nextVehicleId) transaction.setSummary(summaryItems);
        transaction.set(eventRef, {
          type: nextVehicleId ? "DRIVER_VEHICLE_BOUND" : "DRIVER_VEHICLE_UNBOUND",
          eventType: nextVehicleId ? (previousVehicleId ? "DRIVER_VEHICLE_CHANGED" : "DRIVER_VEHICLE_BOUND") : "DRIVER_VEHICLE_UNBOUND",
          driverUid: member.uid, previousVehicleId, nextVehicleId,
          assetIds: [...new Set([previousVehicleId, nextVehicleId].filter(Boolean))],
          previousBinding: { currentVehicleId: previousVehicleId || null },
          nextBinding: { currentVehicleId: nextVehicleId || null },
          actorUid, actorName, actorRole, createdAt: serverTimestamp(),
        });
      });
      if (mounted.current) {
        setEditor(null);
        setMessage(nextVehicleId ? "車輛指派已更新。" : "已解除車輛指派。");
      }
    } catch (err) {
      console.error("[DriverVehicleBinding] save:", err);
      if (mounted.current) setError(err.code === "permission-denied"
        ? "沒有寫入權限，請確認成員資料、車輛摘要與操作歷程的 Firestore 規則。"
        : err.message || "操作失敗，請稍後重試。");
    } finally {
      busy.current = false;
      if (mounted.current) setSaving(false);
    }
  }

  return (
    <section className="driver-vehicle-section" aria-label="成員車輛綁定">
      <style>{`
        .driver-vehicle-section { grid-column: 1 / -1; min-width: 0; padding: 4px 0 18px; border-bottom: 1px solid #E8EDF2; }
        .driver-vehicle-row { display: flex; align-items: center; justify-content: space-between; gap: 16px; flex-wrap: wrap; }
        .driver-vehicle-summary { display: flex; align-items: center; gap: 14px; min-width: 0; flex: 1 1 180px; }
        .driver-vehicle-copy { min-width: 0; }
        .driver-vehicle-label { color: #64748B; font-size: 12px; font-weight: 500; margin-bottom: 4px; }
        .driver-vehicle-value { color: #0F172A; font-size: 17px; font-weight: 650; overflow-wrap: anywhere; }
        .driver-vehicle-value.is-empty { color: #64748B; font-weight: 500; }
        .driver-vehicle-actions { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
        .driver-vehicle-button { border: 1px solid #E2E8F0; border-radius: 10px; background: #FFF; color: #334155; padding: 10px 16px; font: inherit; font-size: 14px; font-weight: 600; cursor: pointer; min-height: 40px; }
        .driver-vehicle-button.is-primary { background: #111827; border-color: #111827; color: #FFF; }
        .driver-vehicle-button.is-quiet { border-color: transparent; background: transparent; color: #64748B; }
        .driver-vehicle-button:hover:not(:disabled) { filter: brightness(.94); }
        .driver-vehicle-button:disabled { opacity: .45; cursor: not-allowed; }
        .driver-vehicle-editor { padding: 24px; background: #FFF; display: flex; flex-direction: column; gap: 14px; max-height: min(85dvh, 820px); box-sizing: border-box; }
        .driver-vehicle-editor-row { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
        .driver-vehicle-select { flex: 0 0 auto; min-width: 0; max-width: 100%; height: 42px; box-sizing: border-box; padding: 0 12px; border: 1px solid #CBD5E1; border-radius: 8px; background: #FFF; color: #0F172A; font: inherit; font-size: 14px; }
        .driver-vehicle-dialog { padding: 0; border: 0; border-radius: 18px; width: min(680px, calc(100vw - 32px)); max-width: none; max-height: 90dvh; box-shadow: 0 24px 80px #0f172a33; color: #0f172a; }
        .driver-vehicle-dialog::backdrop { background: #0f172a66; }
        .driver-vehicle-dialog-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; }
        .driver-vehicle-dialog-header h2 { margin: 0; font-size: 22px; }
        .driver-vehicle-dialog-header p { margin: 6px 0 0; color: #64748b; font-size: 14px; }
        .driver-vehicle-dialog .driver-vehicle-editor-row { justify-content: flex-end; border-top: 1px solid #e8edf2; padding-top: 14px; }
        @media(max-width: 640px) { .driver-vehicle-dialog .driver-vehicle-select { flex-basis: auto; width: 100%; } .driver-vehicle-dialog .driver-vehicle-editor { padding: 18px; } }
        .driver-vehicle-cards { display: grid; gap: 8px; min-height: 0; max-height: 52dvh; overflow-y: auto; overscroll-behavior: contain; flex: 1 1 auto; }
        .driver-vehicle-card { display: flex; align-items: flex-start; gap: 10px; padding: 10px 12px; background: #fff; border: 1px solid #dbe3eb; border-radius: 10px; cursor: pointer; }
        .driver-vehicle-card.is-occupied, .driver-vehicle-card.is-inactive { background: #f1f4f7; color: #64748b; }
        .driver-vehicle-card.is-selected { border-color: #334155; box-shadow: inset 0 0 0 1px #334155; }
        .driver-vehicle-card input { margin-top: 4px; flex: 0 0 auto; }
        .driver-vehicle-card-body { display: grid; gap: 4px; min-width: 0; overflow-wrap: anywhere; font-size: 13px; }
        .driver-vehicle-card-body strong { font-size: 15px; }
        .driver-vehicle-card-heading { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
        .driver-vehicle-card-body .driver-vehicle-plate { display: inline-block; padding: 3px 8px; border: 1px solid #cbd5df; border-radius: 5px; background: #f7f9fb; color: #263746; font-family: ui-monospace, SFMono-Regular, Consolas, monospace; font-size: 15px; font-weight: 700; letter-spacing: .045em; line-height: 1.35; }
        .driver-vehicle-current { display: flex; align-items: center; gap: 9px; min-width: 0; max-width: 100%; white-space: nowrap; }
        .driver-vehicle-current-plate { display: inline-flex; flex: 0 0 auto; align-items: center; gap: 6px; padding: 3px 8px; border: 1px solid #cbd5df; border-radius: 6px; background: linear-gradient(180deg, #fff 0%, #f7f9fb 100%); color: #263746; font-family: ui-monospace, SFMono-Regular, Consolas, monospace; font-size: 15px; font-weight: 700; letter-spacing: .045em; line-height: 1.35; box-shadow: 0 1px 2px #0f172a0a; }
        .driver-vehicle-current-plate::before { content: ""; width: 5px; height: 5px; border-radius: 50%; background: #d89057; opacity: .8; }
        .driver-vehicle-current-model { min-width: 0; overflow: hidden; color: #64748b; font-size: 13px; font-weight: 500; text-overflow: ellipsis; white-space: nowrap; }
        .driver-vehicle-attachments { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 7px; }
        .driver-vehicle-attachment { display: inline-flex; align-items: center; gap: 7px; max-width: 100%; padding: 4px 8px; border: 1px solid #e2e8ef; border-radius: 7px; background: #f7f9fb; }
        .driver-vehicle-attachment.is-trailer { border-color: #e8ddcd; background: #fcf8f2; }
        .driver-vehicle-attachment.is-container { border-color: #d7e8e5; background: #f3f9f8; }
        .driver-vehicle-attachment-kind { color: #74818a; font-size: 10px; font-weight: 700; }
        .driver-vehicle-attachment.is-trailer .driver-vehicle-attachment-kind { color: #98774f; }
        .driver-vehicle-attachment.is-container .driver-vehicle-attachment-kind { color: #548078; }
        .driver-vehicle-attachment-name { overflow: hidden; color: #40505a; font-size: 12px; font-weight: 650; text-overflow: ellipsis; white-space: nowrap; }
        .driver-vehicle-model { color: #64748b; font-size: 13px; }
        .driver-vehicle-card-status { font-weight: 600; }
        .driver-vehicle-feedback { margin-top: 10px; font-size: 13px; line-height: 1.6; }
        @media (max-width: 640px) { .driver-vehicle-actions { margin-left: auto; } .driver-vehicle-select { flex-basis: 100%; width: 100%; } }
      `}</style>
      <div className="driver-vehicle-row">
        <div className="driver-vehicle-summary">
          <div className="driver-vehicle-copy">
            <div className="driver-vehicle-label">使用車輛</div>
            <div className={`driver-vehicle-value ${activeVehicleId ? "" : "is-empty"}`}>
              {!loaded ? "讀取中…" : activeVehicleId ? currentVehicle ? <span className="driver-vehicle-current">{currentVehicle.plateNumber ? <span className="driver-vehicle-current-plate">{currentVehicle.plateNumber}</span> : <span>{currentVehicle.displayName || currentVehicle.assetCode || currentVehicle.id}</span>}{[currentVehicle.make, currentVehicle.model].filter(Boolean).length ? <span className="driver-vehicle-current-model">{[currentVehicle.make, currentVehicle.model].filter(Boolean).join(" ")}</span> : null}</span> : currentLabel || activeVehicleId : "尚未指派"}
            </div>
            {activeVehicleId && currentAttachments.length ? <div className="driver-vehicle-attachments" aria-label="已掛載拖車與容器">{currentAttachments.map((item) => <span key={`${item.kind}-${item.id}`} className={`driver-vehicle-attachment is-${item.kind}`}><span className="driver-vehicle-attachment-kind">{item.label}</span><span className="driver-vehicle-attachment-name">{item.plateNumber || item.displayName || (item.exists ? item.id : "資產已不存在")}</span></span>)}</div> : null}
          </div>
        </div>
        {!editor ? (
          <div className="driver-vehicle-actions">
            {activeVehicleId ? <button type="button" className="driver-vehicle-button is-quiet" disabled={!loaded || saving}
              onClick={() => saveBinding(null, currentVehicleVersion(binding))}>{saving ? "處理中…" : "解除"}</button> : null}
            <button type="button" className={`driver-vehicle-button ${activeVehicleId ? "" : "is-primary"}`}
              ref={pickerTrigger} disabled={!loaded || saving} onClick={openEditor}>
              {activeVehicleId ? "更換車輛" : "指派車輛"}
            </button>
          </div>
        ) : null}
      </div>
      {editor ? (
        <dialog ref={pickerDialog} className="driver-vehicle-dialog" aria-labelledby="vehicle-picker-title"
          onCancel={event => { event.preventDefault(); if (!busy.current) closeEditor(); }}
          onClick={event => { if (event.target === event.currentTarget && !busy.current) closeEditor(); }}>
        <div className="driver-vehicle-editor">
          <header className="driver-vehicle-dialog-header">
            <div><h2 id="vehicle-picker-title">選擇車輛</h2><p>指派給 {member.displayName}</p></div>
            <button type="button" className="driver-vehicle-button is-quiet" aria-label="關閉選車視窗" disabled={saving} onClick={closeEditor}>✕</button>
          </header>
          <input className="driver-vehicle-select" type="search" value={vehicleSearch}
            aria-label="搜尋車牌、成員、廠牌或掛載資產" placeholder="搜尋車牌、成員、廠牌或掛載資產"
            disabled={saving || loadingOptions || !optionsLoaded}
            onChange={(event) => setVehicleSearch(event.target.value)} />
          <div id={`driver-vehicle-${member.uid}`} className="driver-vehicle-cards" role="group" aria-label="車輛與掛載清單">
            {loadingOptions ? <div role="status">載入車輛中…</div> : filteredVehicles.map(item => {
              const assigned = driversFor(item.id);
              const isCurrentAssignment = item.id === activeVehicleId;
              const effectiveStatus = normalizeText(item.status).toUpperCase();
              const unavailable = !isCurrentAssignment && effectiveStatus !== "PARKED";
              const assignedNames = assigned.map(driver => driver.name).filter(Boolean);
              const assignedName = item.currentDriverName || assignedNames.join("、");
              const statusLabel = effectiveStatus === "PARKED" ? "停放中" : effectiveStatus === "IN_USE" ? (isCurrentAssignment ? "使用中" : "使用中（不可新指派）") : effectiveStatus === "MAINTENANCE" ? "維修中" : "已報廢";
              return <label key={item.id} className={`driver-vehicle-card ${assigned.length ? "is-occupied" : ""} ${unavailable ? "is-inactive" : ""} ${vehicleId === item.id ? "is-selected" : ""}`}>
                <input type="radio" name={`vehicle-choice-${member.uid}`} value={item.id} checked={vehicleId === item.id}
                  disabled={saving || unavailable} onChange={() => setVehicleId(item.id)} />
                <div className="driver-vehicle-card-body">
                  <div className="driver-vehicle-card-heading">
                    {item.plateNumber ? <strong className="driver-vehicle-plate">{item.plateNumber}</strong> : <strong>{item.displayName || item.id}</strong>}
                    {[item.make, item.model].filter(Boolean).length > 0 ? <span className="driver-vehicle-model">{[item.make, item.model].filter(Boolean).join(" ")}</span> : null}
                  </div>
                  <span className="driver-vehicle-card-status">{[statusLabel, isCurrentAssignment ? "目前綁定" : "",
                    effectiveStatus === "IN_USE" && assignedName ? `使用成員：${assignedName}` : ""].filter(Boolean).join(" · ")}</span>
                  {item.trailerIds?.length > 0 ? <span>拖架：{item.trailerIds.map(attachmentLabel).join("、")}</span> : null}
                  {item.containerIds?.length > 0 ? <span>容器：{item.containerIds.map(attachmentLabel).join("、")}</span> : null}
                </div>
              </label>;
            })}
          </div>
          {vehicleId ? <div className="driver-vehicle-label">已選擇：{vehicles.find(item => item.id === vehicleId) ? bindingVehicleLabel(vehicles.find(item => item.id === vehicleId)) : currentLabel || vehicleId}</div> : null}
          <div className="driver-vehicle-editor-row">
            <button type="button" className="driver-vehicle-button is-primary"
              disabled={!loaded || saving || loadingOptions || !optionsLoaded || !vehicleId || vehicleId === activeVehicleId || !vehicles.some((item) => item.id === vehicleId && item.status === "PARKED")}
              onClick={() => saveBinding(vehicleId, editor.version)}>{saving ? "儲存中…" : "確認指派"}</button>
            <button type="button" className="driver-vehicle-button is-quiet" disabled={saving}
              onClick={closeEditor}>取消</button>
          </div>
          {optionsError ? <div role="alert" className="driver-vehicle-feedback">
            <span style={{ color: "#B91C1C" }}>{optionsError}</span>
            <button type="button" className="driver-vehicle-button is-quiet" disabled={loadingOptions || saving} onClick={loadVehicleOptions}>重新載入</button>
          </div> : null}
          {optionsLoaded && vehicles.length > 0 && !filteredVehicles.length ? <div className="driver-vehicle-label">找不到符合搜尋條件的車輛。</div> : null}
          {optionsLoaded && !loadingOptions && !vehicles.length ? <div className="driver-vehicle-label">目前沒有車輛，請先至車輛管理新增。</div> : null}
          {error ? <div role="alert" className="driver-vehicle-feedback" style={{ color: "#B91C1C" }}>{error}</div> : null}
        </div>
        </dialog>
      ) : null}
      {!editor && error ? <div role="alert" className="driver-vehicle-feedback" style={{ color: "#B91C1C" }}>{error}</div> : null}
      {message ? <div role="status" className="driver-vehicle-feedback" style={{ color: "#15803D" }}>{message}</div> : null}
    </section>
  );
}

function SecureField({
  label,
  value,
  onChange,
  placeholder,
  visible,
  onToggleVisible,
  onUserEdit,
}) {
  return (
    <div style={styles.fieldWrap}>
      <div style={styles.fieldLabel}>{label}</div>
      <div style={styles.secureWrap}>
        <input
          value={value}
          onChange={(e) => {
            onUserEdit?.();
            onChange(e.target.value);
          }}
          type={visible ? "text" : "password"}
          placeholder={placeholder}
          style={styles.secureInputInner}
        />
        <button type="button" style={styles.eyeBtn} onClick={onToggleVisible}>
          {visible ? <EyeOffIcon /> : <EyeIcon />}
        </button>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={styles.fieldWrap}>
      <div style={styles.fieldLabel}>{label}</div>
      {children}
    </div>
  );
}

function normalizeMember(raw) {
  const displayName = str(raw.displayName || raw.name || "未命名成員");
  const phone = str(raw.phone || raw.phoneNumber || "");
  const email = str(raw.email || raw.account || "");
  const photo = raw.photo && typeof raw.photo === "object" ? raw.photo : null;
  const roleKey = normalizeRole(raw.role || raw.memberRole || raw.orgRole);

  const createdText = formatDate(
    raw.createdAt || raw.joinedAt || raw.updatedAt,
  );

  return {
    id: str(raw.id),
    uid: str(raw.uid || raw.id),
    currentVehicleId: str(raw.currentVehicleId || ""),
    displayName,
    phone,
    email,
    photo,
    idNumber: str(raw.idNumber),
    roleKey,
    roleText: memberRoleLabel(roleKey),
    avatarBg: pickAvatarColor(raw.__index || 0),
    createdText,
    raw,
  };
}

function normalizeRole(value) {
  const role = str(value).toLowerCase();
  if (role === "owner" || role === "primary" || role === "primary_admin") {
    return "owner";
  }
  if (role === "deputy" || role === "deputy_admin") return "deputy";
  return "driver";
}

function memberRoleLabel(role) {
  if (role === "owner") return "主管理員";
  if (role === "deputy") return "副管理員";
  return "成員";
}

function integerValue(value, fallback = 0) {
  const number = Number(value);
  return Number.isFinite(number) ? Math.max(0, Math.floor(number)) : fallback;
}

function buildMemberQuotas(organization, contract, members) {
  const limits = organization?.limits || {};
  const contractAdmin = contract?.admin || {};
  const contractDriver = contract?.driver || {};
  const summary = organization?.contractSummary || {};
  const summaryAdmin = summary.adminPricing || {};
  const summaryDriver = summary.driverPricing || {};
  const activeMembers = members.filter(
    (member) => str(member.status).toLowerCase() === "active",
  );
  const roleOf = (member) => {
    const role = str(member.role || member.memberRole || member.orgRole).toLowerCase();
    if (["owner", "primary", "primary_admin"].includes(role)) return "owner";
    if (["deputy", "deputy_admin"].includes(role)) return "deputy";
    return role === "driver" ? "driver" : "";
  };
  const ownerUsed = activeMembers.filter((member) => roleOf(member) === "owner").length;
  const deputyUsed = activeMembers.filter((member) => roleOf(member) === "deputy").length;
  const driverUsed = activeMembers.filter((member) => roleOf(member) === "driver").length;

  const ownerLimit = integerValue(
    limits.maxPrimaryAdmins,
    integerValue(summaryAdmin.ownerSeats, 1),
  );
  const deputyLimit = integerValue(
    contractAdmin.maxDeputyAdmins,
    integerValue(
      limits.maxDeputyAdmins,
      integerValue(summaryAdmin.deputySeats),
    ),
  );
  const driverLimit = integerValue(
    contractDriver.maxDrivers,
    integerValue(limits.maxDrivers, integerValue(summaryDriver.maxDrivers)),
  );

  return {
    owner: { used: ownerUsed, limit: ownerLimit },
    deputy: { used: deputyUsed, limit: deputyLimit },
    driver: { used: driverUsed, limit: driverLimit },
  };
}

function quotaText(quota, loading = false, error = false) {
  if (error) return "讀取失敗";
  if (loading || !quota) return "讀取中…";
  return `${quota.used}/${quota.limit}`;
}

function looksLikeEmail(v) {
  const s = String(v || "").trim();
  return !!s && s.includes("@") && s.includes(".");
}

function normalizeText(v) {
  return String(v || "").trim();
}

function str(v) {
  return String(v ?? "").trim();
}

function makeMemberPhotoUuid() {
  const cryptoApi = typeof window !== "undefined" ? window.crypto : null;
  if (typeof cryptoApi?.randomUUID === "function") return cryptoApi.randomUUID();
  if (typeof cryptoApi?.getRandomValues !== "function") {
    throw new Error("此環境不支援安全亂數，無法產生成員照片 UUID。");
  }
  const bytes = new Uint8Array(16);
  cryptoApi.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = [...bytes].map((byte) => byte.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function compressMemberPhoto(file, maxDimension, quality, scaleFactor = 1) {
  return new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      const baseScale = Math.min(1, maxDimension / Math.max(image.naturalWidth, image.naturalHeight));
      const scale = baseScale * scaleFactor;
      const canvas = document.createElement("canvas");
      canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
      canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
      const context = canvas.getContext("2d");
      if (!context) {
        URL.revokeObjectURL(objectUrl);
        reject(new Error("無法處理照片，請改用其他圖片檔案。"));
        return;
      }
      context.fillStyle = "#f2f3f5";
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.drawImage(image, 0, 0, canvas.width, canvas.height);
      canvas.toBlob(
        (blob) => {
          URL.revokeObjectURL(objectUrl);
          if (blob) resolve(blob);
          else reject(new Error("照片壓縮失敗。"));
        },
        "image/jpeg",
        quality,
      );
    };
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("無法讀取照片。"));
    };
    image.src = objectUrl;
  });
}

function formatDate(v) {
  if (!v) return "—";
  let d = null;

  if (typeof v?.toDate === "function") d = v.toDate();
  else if (v instanceof Date) d = v;
  else {
    const tryDate = new Date(v);
    if (!Number.isNaN(tryDate.getTime())) d = tryDate;
  }

  if (!d) return "—";

  return `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, "0")}/${String(
    d.getDate(),
  ).padStart(2, "0")}`;
}

function pickAvatarColor(index) {
  const palette = [
    "#2563EB",
    "#F97316",
    "#7C3AED",
    "#16A34A",
    "#DC2626",
    "#0891B2",
    "#D97706",
    "#4F46E5",
  ];
  return palette[index % palette.length];
}

function InfoCard({ title, rows, full = false }) {
  return (
    <div style={{ ...styles.infoCard, ...(full ? styles.infoCardFull : {}) }}>
      <div style={styles.infoCardTitle}>{title}</div>
      <div style={styles.infoRows}>
        {rows.map((row) => (
          <div key={row.label} style={styles.infoRow}>
            <div style={styles.infoLabel}>{row.label}</div>
            <div style={styles.infoValue}>{row.value || "—"}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function QuotaItem({ role, quota, loading }) {
  const isFull = quota && quota.used >= quota.limit;

  return (
    <div
      style={{ ...styles.quotaItem, ...(isFull ? styles.quotaItemFull : {}) }}
    >
      <RoleBadge role={role} />
      <strong>{quotaText(quota, loading)}</strong>
    </div>
  );
}

function RoleBadge({ role }) {
  const stylesByRole = {
    owner: {
      text: "主管理員",
      background: "#F3E8FF",
      color: "#7E22CE",
      border: "#E9D5FF",
    },
    deputy: {
      text: "副管理員",
      background: "#E0F2FE",
      color: "#0369A1",
      border: "#BAE6FD",
    },
    driver: {
      text: "成員",
      background: "#FFF7ED",
      color: "#C2410C",
      border: "#FED7AA",
    },
  };
  const item = stylesByRole[normalizeRole(role)] || stylesByRole.driver;

  return (
    <span
      style={{
        ...styles.roleBadge,
        background: item.background,
        color: item.color,
        borderColor: item.border,
      }}
    >
      {item.text}
    </span>
  );
}

function SearchIcon() {
  return (
    <svg viewBox="0 0 24 24" style={styles.icon}>
      <path
        d="M11 5a6 6 0 1 1 0 12 6 6 0 0 1 0-12Zm8 14-3.2-3.2"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ChevronRightIcon() {
  return (
    <svg viewBox="0 0 24 24" style={styles.chevronIcon}>
      <path
        d="M9 6l6 6-6 6"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg viewBox="0 0 24 24" style={styles.icon}>
      <path
        d="M6 6l12 12M18 6L6 18"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}

function EyeIcon() {
  return (
    <svg viewBox="0 0 24 24" style={styles.icon}>
      <path
        d="M2.5 12s3.5-6 9.5-6 9.5 6 9.5 6-3.5 6-9.5 6-9.5-6-9.5-6Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
      />
      <circle
        cx="12"
        cy="12"
        r="2.6"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
      />
    </svg>
  );
}

function EyeOffIcon() {
  return (
    <svg viewBox="0 0 24 24" style={styles.icon}>
      <path
        d="M3 3l18 18M10.6 10.6A2.5 2.5 0 0 0 14 14M9.2 5.4A11 11 0 0 1 12 5c6 0 9.5 7 9.5 7a16.8 16.8 0 0 1-3.1 4.1M6.3 8.1A16.6 16.6 0 0 0 2.5 12S6 19 12 19a10.8 10.8 0 0 0 3.3-.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

const styles = {
  root: {
    height: "100vh",
    minHeight: 0,
    margin: -22,
    background: "#fff",
    padding: 0,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    boxSizing: "border-box",
  },
  notice: {
    marginBottom: 14,
    padding: "11px 14px",
    borderRadius: 12,
    background: "#ECFDF5",
    border: "1px solid #A7F3D0",
    color: "#047857",
    fontSize: 13,
    fontWeight: 800,
  },
  quotaBar: {
    marginBottom: 16,
    padding: "14px 16px",
    borderRadius: 18,
    background: "#fff",
    border: "1px solid rgba(148,163,184,0.12)",
    boxShadow: "0 8px 22px rgba(15,23,42,0.04)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 16,
    flexWrap: "wrap",
  },
  quotaHeading: {
    display: "flex",
    alignItems: "baseline",
    gap: 9,
  },
  quotaHeadingTitle: {
    color: "#111827",
    fontSize: 18,
    fontWeight: 900,
  },
  quotaHeadingHint: {
    color: "#94A3B8",
    fontSize: 13,
    fontWeight: 700,
  },
  quotaItems: {
    display: "flex",
    alignItems: "center",
    gap: 9,
    flexWrap: "wrap",
  },
  quotaItem: {
    minHeight: 38,
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "0 10px",
    borderRadius: 12,
    background: "#F8FAFC",
    border: "1px solid #E5E7EB",
  },
  quotaItemFull: {
    background: "#FFF7ED",
    borderColor: "#FED7AA",
  },

  centerWrap: {
    minHeight: "60vh",
    background: "#fff",
    borderRadius: 24,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    border: "1px solid rgba(148,163,184,0.14)",
  },
  centerTitle: {
    fontSize: 20,
    fontWeight: 900,
    color: "#111827",
  },
  centerSub: {
    marginTop: 8,
    fontSize: 13,
    color: "#6B7280",
    fontWeight: 700,
  },

  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 16,
    flexWrap: "wrap",
    marginBottom: 16,
  },
  headerTitle: {
    fontSize: 31,
    fontWeight: 900,
    color: "#111827",
    letterSpacing: -0.4,
  },

  mainGrid: {
    width: "100%",
    height: "100%",
    display: "grid",
    gridTemplateColumns: "30% minmax(0, 70%)",
    gap: 0,
    alignItems: "stretch",
    flex: "1 1 auto",
    minHeight: 0,
    overflow: "hidden",
    borderRadius: 24,
    background: "#fff",
    border: "1px solid rgba(148,163,184,0.12)",
    boxShadow: "0 12px 28px rgba(15,23,42,0.05)",
  },

  leftPanel: {
    height: "100%",
    minHeight: 0,
    background: "#fff",
    borderRadius: "24px 0 0 24px",
    padding: 18,
    borderRight: "1px solid #EDF0F3",
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
    overflow: "hidden",
  },
  rightPanel: {
    height: "100%",
    minHeight: 0,
    overflow: "hidden",
    background: "#fff",
    borderRadius: "0 24px 24px 0",
    padding: 20,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
  },

  detailScrollArea: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    overflowX: "hidden",
    paddingRight: 4,
    WebkitOverflowScrolling: "touch",
    scrollbarWidth: "thin",
    scrollbarColor: "#AEB9C1 transparent",
  },

  listTopRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-end",
    gap: 12,
    marginBottom: 14,
  },
  listTitleWrap: {
    minWidth: 0,
    flex: 1,
  },
  listTitleRow: {
    width: "100%",
    minWidth: 0,
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) auto auto",
    alignItems: "center",
    gap: 10,
  },

  panelTitle: {
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    fontSize: 25,
    fontWeight: 900,
    color: "#111827",
  },

  headerActions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 10,
    minWidth: 0,
    flexShrink: 0,
  },

  primaryBtn: {
    height: 38,
    padding: "0 14px",
    borderRadius: 12,
    border: "1px solid rgba(232,120,32,0.24)",
    background: "#111827",
    color: "#fff",
    fontSize: 14,
    fontWeight: 900,
    whiteSpace: "nowrap",
    cursor: "pointer",
    boxShadow: "0 8px 18px rgba(15,23,42,0.08)",
  },

  inlineCountPill: {
    minHeight: 30,
    padding: "0 12px",
    borderRadius: 999,
    display: "inline-flex",
    alignItems: "center",
    background: "#F8FAFC",
    border: "1px solid rgba(148,163,184,0.14)",
    color: "#475569",
    fontSize: 14,
    fontWeight: 900,
  },

  filterBarSingle: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: 10,
    marginBottom: 12,
  },
  searchWrap: {
    minHeight: 46,
    borderRadius: 14,
    border: "1px solid #E5E7EB",
    background: "#fff",
    padding: "0 12px",
    display: "flex",
    alignItems: "center",
    gap: 8,
    color: "#64748B",
  },
  searchInput: {
    flex: 1,
    minWidth: 0,
    border: "none",
    outline: "none",
    background: "transparent",
    color: "#111827",
    fontSize: 15,
    fontWeight: 800,
  },
  clearSearchBtn: {
    border: "none",
    background: "transparent",
    padding: 0,
    cursor: "pointer",
    color: "#94A3B8",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  listScrollArea: {
    flex: 1,
    minHeight: 0,
    height: 0,
    overflowY: "auto",
    paddingRight: 4,
    paddingBottom: 50,
    boxSizing: "border-box",
    scrollbarWidth: "thin",
    scrollbarColor: "#AEB9C1 transparent",
  },
  loadingBox: {
    minHeight: 360,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
  },
  loadingText: {
    fontSize: 14,
    color: "#6B7280",
    fontWeight: 800,
  },
  emptyBox: {
    minHeight: 240,
    borderRadius: 18,
    border: "1px dashed #D1D5DB",
    background: "#F9FAFB",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: 20,
  },
  emptyDetailWrap: {
    minHeight: 520,
    borderRadius: 18,
    border: "1px dashed #D1D5DB",
    background: "#F9FAFB",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: 20,
  },
  emptyTitle: {
    fontSize: 18,
    fontWeight: 900,
    color: "#111827",
  },
  emptySub: {
    marginTop: 8,
    fontSize: 13,
    color: "#6B7280",
    fontWeight: 700,
    textAlign: "center",
    lineHeight: 1.7,
  },

  driverList: {
    display: "grid",
    gap: 10,
    paddingRight: 4,
  },
  driverRow: {
    width: "100%",
    border: "1px solid rgba(148,163,184,0.12)",
    background: "#F8FAFC",
    borderRadius: 18,
    padding: 12,
    display: "flex",
    alignItems: "center",
    gap: 10,
    cursor: "pointer",
    transition: "all 180ms ease",
  },
  driverRowActive: {
    background: "#FFF7ED",
    border: "1px solid rgba(232,120,32,0.24)",
    boxShadow: "0 10px 24px rgba(232,120,32,0.08)",
  },
  driverRowLeft: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    flex: 1,
    minWidth: 0,
  },

  avatar: {
    width: 46,
    height: 46,
    borderRadius: 23,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    flexShrink: 0,
  },
  bigAvatar: {
    width: 72,
    height: 72,
    borderRadius: 36,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    flexShrink: 0,
  },
  avatarImg: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  avatarText: {
    color: "#fff",
    fontSize: 18,
    fontWeight: 900,
  },
  photoEditRow: {
    display: "grid",
    gridTemplateColumns: "72px minmax(0, 1fr) auto",
    alignItems: "center",
    gap: 14,
    padding: 14,
    borderRadius: 18,
    background: "#F8FAFC",
    border: "1px solid #EEF2F7",
  },
  photoEditPreview: {
    width: 72,
    height: 72,
    borderRadius: 36,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    flexShrink: 0,
    background: "#f2f3f5",
  },
  photoEditImg: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  photoEditCopy: {
    display: "grid",
    gap: 5,
    minWidth: 0,
  },
  photoEditCopyText: {
    color: "#64748B",
    fontSize: 12,
    fontWeight: 700,
  },
  hiddenFileInput: {
    display: "none",
  },
  readOnlyRole: {
    minHeight: 46,
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "0 12px",
    borderRadius: 14,
    border: "1px solid #E5E7EB",
    background: "#F8FAFC",
    color: "#64748B",
    fontSize: 12,
    fontWeight: 700,
  },
  bigAvatarText: {
    color: "#fff",
    fontSize: 28,
    fontWeight: 900,
  },

  driverNameRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
  },
  driverName: {
    fontSize: 17,
    fontWeight: 900,
    color: "#111827",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  driverMeta: {
    marginTop: 5,
    fontSize: 14,
    color: "#374151",
    fontWeight: 800,
  },
  driverSubLine: {
    marginTop: 3,
    fontSize: 13,
    color: "#6B7280",
    fontWeight: 700,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  detailHero: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 16,
    flex: "0 0 auto",
    flexWrap: "wrap",
    paddingBottom: 18,
    borderBottom: "1px solid #EEF2F7",
  },
  detailHeroLeft: {
    display: "flex",
    alignItems: "center",
    gap: 14,
    minWidth: 0,
  },
  detailNameRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    flexWrap: "wrap",
  },
  detailName: {
    margin: 0,
    fontSize: 31,
    fontWeight: 900,
    color: "#111827",
    letterSpacing: -0.4,
  },
  detailRole: {
    marginTop: 6,
    fontSize: 15,
    color: "#6B7280",
    fontWeight: 800,
  },
  detailEmailLine: {
    marginTop: 6,
    fontSize: 14,
    color: "#64748B",
    fontWeight: 700,
  },
  detailHeroActions: {
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
  },
  softBtn: {
    height: 42,
    padding: "0 14px",
    borderRadius: 14,
    border: "1px solid rgba(148,163,184,0.14)",
    background: "#F8FAFC",
    color: "#111827",
    fontSize: 15,
    fontWeight: 800,
    cursor: "pointer",
  },

  detailGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 14,
    marginTop: 18,
  },
  infoCard: {
    background: "#F8FAFC",
    borderRadius: 20,
    padding: 16,
    border: "1px solid rgba(148,163,184,0.10)",
  },
  infoCardFull: {
    gridColumn: "1 / -1",
  },
  infoCardTitle: {
    fontSize: 16,
    fontWeight: 900,
    color: "#111827",
    marginBottom: 10,
  },
  infoRows: {
    display: "grid",
    gap: 10,
  },
  infoRow: {
    display: "grid",
    gridTemplateColumns: "110px 1fr",
    gap: 10,
    alignItems: "start",
    paddingBottom: 10,
    borderBottom: "1px solid #EEF2F7",
  },
  infoLabel: {
    fontSize: 14,
    color: "#6B7280",
    fontWeight: 800,
  },
  infoValue: {
    fontSize: 15,
    color: "#111827",
    fontWeight: 800,
    lineHeight: 1.7,
    wordBreak: "break-word",
  },

  badge: {
    display: "inline-flex",
    alignItems: "center",
    minHeight: 28,
    padding: "0 10px",
    borderRadius: 999,
    border: "1px solid transparent",
    fontSize: 13,
    fontWeight: 900,
  },

  modalBackdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(15,23,42,0.40)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
    zIndex: 1000,
    backdropFilter: "blur(3px)",
  },
  modalCard: {
    width: "100%",
    maxWidth: 720,
    maxHeight: "88vh",
    background: "#fff",
    borderRadius: 26,
    border: "1px solid rgba(148,163,184,0.14)",
    boxShadow: "0 28px 80px rgba(15,23,42,0.20)",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  },
  modalHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 16,
    padding: "22px 22px 18px",
    borderBottom: "1px solid #EEF2F7",
    flexShrink: 0,
  },
  modalTitle: {
    fontSize: 27,
    fontWeight: 900,
    color: "#111827",
  },
  modalCloseBtn: {
    width: 42,
    height: 42,
    borderRadius: 14,
    border: "1px solid rgba(148,163,184,0.14)",
    background: "#F8FAFC",
    color: "#111827",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    flexShrink: 0,
  },
  modalHint: {
    padding: "16px 22px 0",
    color: "#6B7280",
    fontWeight: 700,
    fontSize: 14,
    lineHeight: 1.7,
    flexShrink: 0,
  },
  roleSelectTitle: {
    marginBottom: 9,
    color: "#111827",
    fontSize: 15,
    fontWeight: 900,
  },
  roleChoices: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: 8,
    marginBottom: 16,
  },
  roleChoice: {
    minHeight: 60,
    border: "1px solid #E5E7EB",
    borderRadius: 12,
    background: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    gap: 5,
    cursor: "pointer",
  },
  roleChoiceActive: {
    border: "1px solid rgba(232,120,32,0.48)",
    background: "#FFF7ED",
    boxShadow: "0 6px 14px rgba(232,120,32,0.1)",
  },
  roleChoiceDisabled: {
    opacity: 0.55,
    cursor: "not-allowed",
    background: "#F8FAFC",
  },
  roleChoiceTopline: {
    display: "flex",
    alignItems: "center",
    gap: 7,
  },
  roleBadge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 22,
    padding: "0 7px",
    border: "1px solid",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 900,
    whiteSpace: "nowrap",
  },
  modalBody: {
    padding: 22,
    display: "grid",
    gap: 14,
    overflowY: "auto",
  },
  modalGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 14,
  },
  modalFooter: {
    padding: 20,
    borderTop: "1px solid #EEF2F7",
    display: "flex",
    justifyContent: "flex-end",
    gap: 10,
    background: "#fff",
    flexShrink: 0,
  },

  fieldWrap: {
    display: "grid",
    gap: 6,
  },
  fieldLabel: {
    fontSize: 14,
    fontWeight: 800,
    color: "#6B7280",
  },
  input: {
    width: "100%",
    minHeight: 46,
    borderRadius: 14,
    border: "1px solid #E5E7EB",
    background: "#fff",
    padding: "0 12px",
    fontSize: 13,
    fontWeight: 800,
    color: "#111827",
    outline: "none",
    boxSizing: "border-box",
  },
  inputDisabled: {
    background: "#F3F4F6",
    borderColor: "#D1D5DB",
    color: "#9CA3AF",
    cursor: "not-allowed",
  },
  secureWrap: {
    border: "1px solid #E5E7EB",
    borderRadius: 12,
    padding: "0 12px",
    minHeight: 46,
    background: "#fff",
    display: "flex",
    alignItems: "center",
    gap: 10,
    boxSizing: "border-box",
  },
  secureInputInner: {
    flex: 1,
    border: "none",
    outline: "none",
    background: "transparent",
    color: "#111827",
    fontSize: 13,
    fontWeight: 800,
    minWidth: 0,
  },
  eyeBtn: {
    border: "none",
    background: "transparent",
    padding: 0,
    cursor: "pointer",
    color: "#6B7280",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },

  cancelBtn: {
    height: 44,
    padding: "0 16px",
    borderRadius: 14,
    border: "1px solid #E5E7EB",
    background: "#fff",
    color: "#111827",
    fontSize: 13,
    fontWeight: 900,
    cursor: "pointer",
  },
  confirmBtn: {
    height: 44,
    padding: "0 16px",
    borderRadius: 14,
    border: "1px solid rgba(232,120,32,0.24)",
    background: "#111827",
    color: "#fff",
    fontSize: 13,
    fontWeight: 900,
    cursor: "pointer",
  },
  confirmBtnDisabled: {
    opacity: 0.7,
    cursor: "not-allowed",
  },

  icon: {
    width: 18,
    height: 18,
    display: "block",
    flexShrink: 0,
  },
  chevronIcon: {
    width: 16,
    height: 16,
    color: "#9CA3AF",
    flexShrink: 0,
  },

  spinner: {
    width: 16,
    height: 16,
    borderRadius: "50%",
    border: "2px solid #D1D5DB",
    borderTopColor: "#111827",
    animation: "driverManagerSpin 1s linear infinite",
  },
  spinnerLarge: {
    width: 24,
    height: 24,
    borderRadius: "50%",
    border: "2px solid #D1D5DB",
    borderTopColor: "#111827",
    animation: "driverManagerSpin 1s linear infinite",
    marginBottom: 12,
  },
};

if (
  typeof document !== "undefined" &&
  !document.getElementById("driver-manager-spin-style")
) {
  const style = document.createElement("style");
  style.id = "driver-manager-spin-style";
  style.innerHTML = `
    @keyframes driverManagerSpin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }

    @media (max-width: 1180px) {
      .driver-manager-responsive {}
    }

    @media (max-width: 1024px) {
      body {}
    }
  `;
  document.head.appendChild(style);
}
